/* prefs.svelte.js generated by Svelte v5.56.9 */
import * as $ from 'svelte/internal/client';
import api from "./api.js";

import {
	DEFAULT_PREFS,
	mergePrefs,
	pushRecent,
	readPrefs,
	resolveTheme,
	writePrefs
} from "./storage.js";

import { rememberSearch } from "./search.js";

export const prefs = $.tag_proxy($.proxy({ ...DEFAULT_PREFS }), 'prefs');

let syncTimer;
let syncEnabled = false;

export function loadPrefs() {
	Object.assign(prefs, readPrefs());
	applyTheme();
}

export function applyTheme(prefersDark) {
	if ($.strict_equals(typeof document, "undefined")) return;

	const dark = prefersDark ?? window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
	const resolved = resolveTheme(prefs.theme, dark);

	document.documentElement.dataset.theme = resolved;
}

export function watchSystemTheme() {
	const media = window.matchMedia?.("(prefers-color-scheme: dark)");

	media?.addEventListener?.("change", (event) => applyTheme(event.matches));
}

/** Applies a patch, persists locally, and (debounced) mirrors it to the server. */
export function setPrefs(patch) {
	Object.assign(prefs, mergePrefs(prefs, patch));
	writePrefs(snapshot());

	if ($.strict_equals(prefs.theme, undefined, false)) applyTheme();

	scheduleSync();
}

export function snapshot() {
	return JSON.parse(JSON.stringify(prefs));
}

export function setTheme(theme) {
	setPrefs({ theme });
}

export function cycleTheme() {
	const order = ["system", "light", "dark"];
	const next = order[(order.indexOf(prefs.theme) + 1) % order.length];

	setTheme(next);

	return next;
}

export function rememberSearchTerm(query) {
	setPrefs({ recentSearches: rememberSearch(prefs.recentSearches, query) });
}

export function rememberView(restaurant) {
	if (!restaurant) return;

	setPrefs({
		recentlyViewed: pushRecent(prefs.recentlyViewed, {
			id: restaurant.id,
			slug: restaurant.slug,
			name: restaurant.name,
			cuisine: restaurant.cuisine,
			city: restaurant.city,
			thumbnailUrl: restaurant.thumbnailUrl
		}),
		lastCuisine: restaurant.cuisine ?? prefs.lastCuisine,
		lastCity: restaurant.city ?? prefs.lastCity
	});

	track("view_restaurant", restaurant.id, { cuisine: restaurant.cuisine, city: restaurant.city });
}

export function isFavorite(restaurantId) {
	return prefs.favorites.includes(restaurantId);
}

export function setLocalFavorite(restaurantId, favorite) {
	const next = favorite
		? pushRecent(prefs.favorites.filter((id) => $.strict_equals(id, restaurantId, false)), restaurantId, 200)
		: prefs.favorites.filter((id) => $.strict_equals(id, restaurantId, false));

	setPrefs({ favorites: next });
}

export function dismissTip(id) {
	if (prefs.dismissedTips.includes(id)) return;

	setPrefs({ dismissedTips: [...prefs.dismissedTips, id] });
}

/** Fire-and-forget behaviour signal; failures never surface to the visitor. */
export function track(kind, subjectId, payload) {
	api.recordEvent({ kind, subjectId, payload }).catch(() => {});
}

// --- backend mirroring ------------------------------------------------------
export function enableSync(enabled) {
	syncEnabled = enabled;

	if (enabled) scheduleSync();
}

/** Pulls server-side preferences and merges them under the local ones. */
export async function pullRemotePrefs() {
	try {
		const remote = (await $.track_reactivity_loss(api.getPreferences()))();

		if (remote?.data && Object.keys(remote.data).length > 0) {
			Object.assign(prefs, mergePrefs(prefs, remote.data));
			writePrefs(snapshot());
			applyTheme();
		}
	} catch {
		/* not signed in, or offline — local preferences stand */
	}
}

function scheduleSync() {
	if (!syncEnabled) return;

	clearTimeout(syncTimer);

	syncTimer = setTimeout(
		() => {
			api.mergePreferences(snapshot()).catch(() => {});
		},
		800
	);
}